// components/user-menu.js
// ═══════════════════════════════════════════════════════════
// User menu at bottom of sidebar.
// Owns: dropdown UI, voice toggle display
// Never owns: logout logic, settings logic (calls props)
// Future props: onSettingsOpen, onLanguageOpen, onGuideOpen
// ═══════════════════════════════════════════════════════════
import { useState } from 'react';

export default function UserMenu({ userRecord, supabase, onLogout, onSettingsOpen, onProfileOpen, initialOpen = false, hideTrigger = false }) {
  const [open, setOpen] = useState(initialOpen);

  async function handleLogout() {
    setOpen(false);
    await supabase.auth.signOut();
    if (onLogout) onLogout();
  }

  const name = userRecord?.first_name
    ? `${userRecord.first_name} ${userRecord.last_name || ''}`.trim()
    : 'User';
  const email = userRecord?.email || '';
  const plan = userRecord?.plan || 'Free plan';
  const initial = (userRecord?.first_name || 'U')[0].toUpperCase();

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>

      {/* Backdrop */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 49 }}
        />
      )}

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 8, right: 8,
          background: '#111118', border: '1px solid #2a2a2a',
          borderRadius: 10, overflow: 'hidden', zIndex: 50,
          marginBottom: 4,
        }}>

          {/* Email */}
          <div style={{ padding: '10px 14px 8px', borderBottom: '1px solid #222' }}>
            <div style={{ color: '#555', fontSize: 11, fontFamily: 'Georgia, serif', letterSpacing: '0.02em' }}>
              {email}
            </div>
          </div>

          {/* Language */}
          <MenuItem
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="9" stroke="#888" strokeWidth="1.5"/><path d="M12 3c-2.5 3-4 5.5-4 9s1.5 6 4 9M12 3c2.5 3 4 5.5 4 9s-1.5 6-4 9M3 12h18" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/></svg>}
            label="Language"
            arrow
            onClick={() => {}}
            borderBottom
          />

          {/* Guide */}
          <MenuItem
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 2L2 7v10l10 5 10-5V7L12 2z" stroke="#888" strokeWidth="1.5" strokeLinejoin="round"/><path d="M12 22V12M2 7l10 5 10-5" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/></svg>}
            label="Guide"
            arrow
            onClick={() => {}}
            borderBottom
          />

          {/* Profile */}
          <MenuItem
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="8" r="4" stroke="#888" strokeWidth="1.4"/><path d="M4 21c0-3.87 3.58-7 8-7s8 3.13 8 7" stroke="#888" strokeWidth="1.4" strokeLinecap="round"/></svg>}
            label="Profile"
            onClick={() => { setOpen(false); onProfileOpen?.(); }}
            borderBottom
          />

          {/* Settings */}
          <MenuItem
            icon={<svg width="14" height="14" viewBox="0 0 15 15" fill="none"><circle cx="7.5" cy="7.5" r="2" stroke="#888" strokeWidth="1.2"/><path d="M7.5 1v1.5M7.5 12.5V14M1 7.5h1.5M12.5 7.5H14M2.9 2.9l1.1 1.1M11 11l1.1 1.1M2.9 12.1L4 11M11 4l1.1-1.1" stroke="#888" strokeWidth="1.2" strokeLinecap="round"/></svg>}
            label="Settings"
            onClick={() => { setOpen(false); onSettingsOpen?.(); }}
            borderBottom
          />

          {/* View plan */}
          <MenuItem
            icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="2" y="5" width="20" height="14" rx="2" stroke="#888" strokeWidth="1.5"/><path d="M2 10h20" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/></svg>}
            label="View plan"
            onClick={() => { setOpen(false); }}
            borderBottom
          />

          {/* Log out */}
          <div
            onClick={handleLogout}
            style={{ padding: '8px 14px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
            onMouseEnter={e => e.currentTarget.style.background = '#1e1414'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="#c46050" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ color: '#c46050', fontSize: 13, fontFamily: 'Georgia, serif' }}>Log out</span>
          </div>

        </div>
      )}

      {/* Trigger */}
      {!hideTrigger && (
        <div
          onClick={() => setOpen(v => !v)}
          style={{
            borderTop: '1px solid #272736', padding: '12px 14px',
            display: 'flex', alignItems: 'center', gap: 10,
            cursor: 'pointer',
          }}
          onMouseEnter={e => e.currentTarget.style.background = '#141414'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: '#2a2520', border: '1px solid #3a3020',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <span style={{ color: '#c8a96e', fontSize: '15px', fontFamily: 'Georgia, serif', fontWeight: 'bold' }}>
              {initial}
            </span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#d4d4d4', fontSize: 14, fontFamily: 'Georgia, serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {name}
            </div>
            <div style={{ color: '#555', fontSize: 11, fontFamily: 'Georgia, serif' }}>{plan}</div>
          </div>
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none" opacity="0.4">
            <path d={open ? "M4 10l4-4 4 4" : "M4 6l4 4 4-4"} stroke="#d4d4d4" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, label, arrow, onClick, borderBottom }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '8px 14px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', cursor: 'pointer',
        borderBottom: borderBottom ? '1px solid #1e1e2a' : 'none',
      }}
      onMouseEnter={e => e.currentTarget.style.background = '#272736'}
      onMouseLeave={e => e.currentTarget.style.background = 'none'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        {icon}
        <span style={{ color: '#888', fontSize: 13, fontFamily: 'Georgia, serif' }}>{label}</span>
      </div>
      {arrow && (
        <svg width="12" height="12" viewBox="0 0 16 16" fill="none" opacity="0.4">
          <path d="M6 4l4 4-4 4" stroke="#d4d4d4" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  );
}